import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import api from "../api/http";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => localStorage.getItem("token"));
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const syncToken = () => setToken(localStorage.getItem("token"));
        window.addEventListener("auth-token-changed", syncToken);
        window.addEventListener("storage", syncToken);
        return () => {
            window.removeEventListener("auth-token-changed", syncToken);
            window.removeEventListener("storage", syncToken);
        };
    }, []);

    const refreshUser = useCallback(async () => {
        const res = await api.get("/api/me");
        setUser(res.data.user);
        setProfile(res.data.profile || null);
        return res.data;
    }, []);

    useEffect(() => {
        if (!token) {
            setUser(null);
            setProfile(null);
            setLoading(false);
            return;
        }
        refreshUser()
            .catch(() => {
                localStorage.removeItem("token");
                localStorage.removeItem("refreshToken");
                setToken(null);
                setUser(null);
                setProfile(null);
            })
            .finally(() => setLoading(false));
    }, [token, refreshUser]);

    const login = useCallback((newToken, newRefreshToken) => {
        setLoading(true);
        localStorage.setItem("token", newToken);
        if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);
        setToken(newToken);
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        setToken(null);
        setUser(null);
        setProfile(null);
    }, []);

    const isAuthenticated = !!token;

    const value = useMemo(
        () => ({ token, user, profile, loading, login, logout, refreshUser, isAuthenticated, api }),
        [token, user, profile, loading, login, logout, refreshUser, isAuthenticated]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
    return ctx;
}
